<template>
  <div>
    <div class="row justify-content-between my-3 mx-0">

      <div class="col-sm-auto col-12">
        <b-button
          variant="success"
          class="mobile-btn-100"
          :disabled="saving || selectedCount < 2"
          @click="releaseOrders(true)">
          Autorizar Separação por Romaneio
        </b-button>
      </div>

      <div v-if="saving" class="col-auto">
        <loading-animation />
      </div>

      <div class="col-sm-auto col-12 mt-sm-0 mt-3">
        <b-button
          variant="info"
          class="mobile-btn-100"
          :disabled="saving || selectedCount < 1"
          @click="releaseOrders(false)">
          Autorizar Separação Individual
        </b-button>
      </div>

    </div>

    <b-modal
      id="confirmation-modal"
      title="Autorizar Separação"
      @ok="confirmOrderRelease"
    >
      <template slot="default">
        <p style="white-space: pre-line; text-align: justify;" class="mb-0">Tem certeza de que deseja autorizar a separação <b v-if="batchPicking" class="text-success">por Romaneio</b><b v-else class="text-info">Individual</b> destes <b>{{ selectedCount }}</b> pedidos?</p>
      </template>

      <template slot="modal-footer" slot-scope="{ ok, cancel }">
        <b-button variant="secondary" @click="hideConfirmationModal">Não</b-button>
        <b-button
          :variant="batchPicking ? 'success' : 'info'"
          @click="ok"
        >
          Sim
        </b-button>
      </template>
    </b-modal>

    <b-modal
      size="xl"
      centered
      lazy
      title="Criar romaneio"
      header-bg-variant="info"
      footer-border-variant="info"
      id="batch-picking-confirmation-modal"
      scrollable
    >
      <b-container>
        <div v-if="saving" class="mb-5 pb-5">
          <loading-animation />
        </div>

        <div v-else-if="bpConfirmationData">
          <div class="row align-items-end mb-3">

            <div class="col pl-0">
              <label>Endereço de destino:</label>
              <model-list-select
                :list="locations"
                option-value="id"
                option-text="name"
                v-model="selectedLocation"
                placeholder="Digite para pesquisar..."
                @searchchange="searchLocation($event)"
                :isDisabled="saving"
                :filterPredicate="alwaysTrueFilterPredicate"
              />
            </div>

            <div class="col-auto px-0">
              <div v-if="searchLocationInProcess">
                <loading-animation />
              </div>
              <b-button
                v-else
                variant="secondary"
                @click.prevent="openLocationBarcodeInput()"
                :disabled="saving"
              >
                <i class="fa fa-barcode"></i>
              </b-button>
            </div>

          </div>

          <div class="row mb-1">
            <div class="col-lg col-12">
              <b>Item</b>
            </div>

            <div class="col-xl-2 col-lg-3 col">
              <b>Original</b>
            </div>

            <div class="col-xl-1 col-lg-2 col-auto">
              <b>Agrupado</b>
            </div>
          </div>

          <div
            v-for="(row, index) in bpConfirmationData"
            :key="'bpConfirmationData_' + index"
            class="row border align-items-center py-2"
            :class="{ 'bg-light': index % 2 != 0 }"
          >

            <div class="col-lg col-12">
              {{ getItemLabel(row.item) }}
            </div>

            <div class="col-xl-2 col-lg-3 col p-lg-2 p-3 pr-0">
              <div class="card mb-0">
                <div class="card-body p-1">

                  <div
                    v-for="(oi, bi) in row.before"
                    :key="'bpConfirmationData_' + index + '_before_' + bi"
                    class="row"
                  >

                    <div class="col">
                      <span v-if="oi.order">Pedido {{ oi.order.number || oi.order.documentNumber }}</span>
                    </div>

                    <div class="col-auto">
                      x <b>{{ oi.quantity }}</b>
                    </div>

                  </div>

                </div>
              </div>

            </div>

            <div class="col-xl-1 col-lg-2 col-auto">

              <div class="row align-items-center">

                <div class="col-auto">=</div>
                <div class="col batch-picking-confirmation-grouped-column">{{ row.after.quantity }}</div>

              </div>
            </div>

          </div>

        </div>
      </b-container>

      <template #modal-footer>
        <b-form-checkbox v-model="shouldOpenGeneratedOrder" class="mr-3">
          Abrir pedido gerado
        </b-form-checkbox>
        <b-button
          variant="success"
          class="mobile-btn-100"
          :disabled="saving"
          @click="confirmBatchPicking">
          Confirmar
        </b-button>
      </template>
    </b-modal>

    <barcode-input-modal
      id="location-barcode-input-modal"
      :attempts="locationBarcodeAttempts"
      :loading="loadingLocationBarcode"
      :ok="readLocationBarcode"
    ></barcode-input-modal>

  </div>
</template>

<script>
import { httpClient } from '@/service'
import { LoadingAnimation } from "@/components/loaders";
import shared from '@/shared/shared';
import { ModelListSelect } from 'vue-search-select'
import BarcodeInputModal from "@/components/common/barcode-input-modal";

const DESTINATION_LOCATION_KEY = 'batch-picking-destination-locations-per-warehouse';

export default {
  name: 'batch-picking-buttons',
  components: {
    LoadingAnimation,
    ModelListSelect,
    BarcodeInputModal
  },

  props: {
    selectedOrders: {
      type: Array,
      require
    },
    parameter: {
      type: Object,
      require
    },
    saving: {
      type: Boolean,
      require
    },
    updateSaving: {
      type: Function,
      require
    },
    spliceOrders: {
      type: Function,
      require
    },
    openOrderInfoModal: {
      type: Function,
      require
    }
  },

  data() {
    return {
      batchPicking: undefined,
      bpdto: undefined,
      bpConfirmationData: undefined,
      selectedLocation: {},
      locations: [],
      searchLocationInProcess: false,
      locationSearchTimeout: null,
      loadingLocationBarcode: false,
      locationBarcodeAttempts: 0,
      selectedLocationsPerWarehouse: {},
      shouldOpenGeneratedOrder: true
    }
  },

  mounted() {
    this.selectedLocationsPerWarehouse = shared.getLocalStorageObj(DESTINATION_LOCATION_KEY);
    this.shouldOpenGeneratedOrder = Boolean(shared.getLocalStorageString('batch-picking-buttons/shouldOpenGeneratedOrder'));
  },

  computed: {
    selectedCount() {
      return this.selectedOrders ? this.selectedOrders.length : 0;
    },

    batchPickingWarehouse() {
      if (
        this.bpdto &&
        this.bpdto.resultOrder &&
        this.bpdto.resultOrder.stockLocale &&
        this.bpdto.resultOrder.stockLocale.id
      ) {
        return this.bpdto.resultOrder.stockLocale;
      }

      return undefined;
    }
  },

  methods: {
    releaseOrders(batchPicking) {
      if (!this.parameter) {
        this.$notify.textError('Parâmetros não encontrados');
        return;
      }

      if (!this.parameter.separationPendingStatus && !shared.getTicketStatusFromParameter(this.parameter, 'separation', 'pending')) {
        this.$notify.textError('Situação/Status pendente não informada nos Parâmetros');
        return;
      }

      this.batchPicking = batchPicking;

      if (this.batchPicking) {
        this.confirmOrderRelease();
      } else {
        this.$bvModal.show("confirmation-modal");
      }
    },

    confirmOrderRelease() {
      if (this.saving) return;

      let request;
      if (this.batchPicking) {
        let body = {
          sourceOrders: this.selectedOrders.map(o => {
            return {
              id: o.id
            };
          })
        };

        request = httpClient.post(`${process.env.VUE_APP_API_URL}orders/batch-picking/assemble`, body);
      } else {
        let body = this.selectedOrders.map(o => {
          return {
            id: o.id,
            status: this.parameter.separationPendingStatus
          };
        });

        request = httpClient.put(`${process.env.VUE_APP_API_URL}orders/multiple?patch=true`, body);
      }

      this.updateSaving(true);
      request.then(({ data }) => {
        this.updateSaving(false);

        if (this.batchPicking) {
          this.bpdto = data.data;
          this.setBpConfirmationData();
          this.$bvModal.show("batch-picking-confirmation-modal");
        } else {
          this.$notify.success('Pedidos salvos com sucesso');
          this.spliceSelectedOrders();
        }
      })
      .catch((error) => {
        this.updateSaving(false);
        if (error.message) {
          this.$notify.error(error)
        }
      });
    },

    spliceSelectedOrders() {
      this.spliceOrders(this.selectedOrders);
    },

    hideConfirmationModal() {
      this.$bvModal.hide('confirmation-modal');
    },

    alwaysTrueFilterPredicate() {
      return true;
    },

    setBpConfirmationData() {
      this.bpConfirmationData = [];

      this.bpdto.resultOrder.orderItems.forEach(roi => {
        if (roi.item && roi.item.id) {
          let row = {
            item: roi.item,
            before: [],
            after: {
              id: roi.id,
              quantity: roi.quantity
            }
          };

          this.bpdto.sourceOrders.forEach(so => {
            if (so.orderItems) {
              let soi = so.orderItems.filter(tempOI => tempOI.item && tempOI.item.id === roi.item.id);
              soi = soi.map(oi => {
                return {
                  id: oi.id,
                  quantity: oi.quantity,
                  order: so
                };
              });
              row.before.push(...soi);
            }
          });

          this.bpConfirmationData.push(row);
        }
      });

      this.bpConfirmationData.sort((a, b) => b.before.length - a.before.length);

      this.clearLocations();

      if (this.batchPickingWarehouse) {
        let tempLocation = this.selectedLocationsPerWarehouse[this.batchPickingWarehouse.id];

        if (tempLocation && tempLocation.id) {
          this.selectedLocation = tempLocation;
          this.locations.unshift(this.selectedLocation);
        }
      }
    },

    clearLocations() {
      this.locations = [];
    },

    getItemLabel(item) {
      return shared.getItemLabel(item);
    },

    async searchLocation(searchText) {
      if (!this.searchLocationInProcess && !this.saving && searchText && searchText.length) {
        if (this.locationSearchTimeout) {
          clearTimeout(this.locationSearchTimeout);
        }

        let conditions = [
          {
            field: "name",
            conditionalOperator: "LIKE_START",
            value: searchText,
          },
          {
            field: 'stockLocale.id',
            value: this.batchPickingWarehouse.id
          }
        ];

        this.locationSearchTimeout = setTimeout(() => {
          this.searchLocationInProcess = true;

          httpClient
            .post(
              `${
                process.env.VUE_APP_API_URL
              }locations/condition?page=${0}&size=${10}`,
              {
                conditions: conditions,
              }
            )
            .then(({ data }) => {
              this.searchLocationInProcess = false;

              data.data.content.slice().reverse().forEach(location => {
                let index = this.locations.findIndex(l => l.id === location.id);

                if (index > -1) {
                  this.locations.splice(index, 1);
                }

                this.locations.unshift(location);
              });
            })
            .catch((error) => {
              this.searchLocationInProcess = false;
              if (error.message) {
                this.$notify.error(error);
              }
            });
        }, 500);
      }
    },

    openLocationBarcodeInput() {
      this.$bvModal.show("location-barcode-input-modal");
    },

    readLocationBarcode(barcode) {
      if (!this.loadingLocationBarcode) {
        if (barcode && barcode.length) {
          this.loadingLocationBarcode = true;
          httpClient
            .get(`${process.env.VUE_APP_API_URL}locations/barcode/${barcode}?warehouseId=${this.batchPickingWarehouse.id}`)
            .then((data) => {
              this.loadingLocationBarcode = false;
              let location = data.data.data;

              if (location) {
                if (location.stockLocale.id !== this.batchPickingWarehouse.id) {
                  this.$notify.textError(
                    "Endereço não é compatível com o armazém " + this.bpdto.resultOrder.stockLocale.name
                  );
                } else {
                  this.locations.unshift(location);
                  this.selectedLocation = location;
                  this.$bvModal.hide("location-barcode-input-modal");
                  this.$notify.success("Endereço encontrado");
                }
              } else {
                this.$notify.textError("Endereço não encontrado");
              }

              this.locationBarcodeAttempts++;
            })
            .catch((error) => {
              this.loadingLocationBarcode = false;
              if (error.message) {
                this.$notify.error(error);
              } else {
                this.$notify.textError(
                  "Houve um erro ao buscar o endereço pelo código de barras. Por favor tente novamente"
                );
              }

              this.locationBarcodeAttempts++;
            });
        } else {
          this.$notify.textError(
            "Código inválido. Por favor insira um código de barras válido"
          );
        }
      }

      this.locationBarcodeAttempts++;
    },

    validateLocationItem(location) {
      if (location.item && location.item.id) {
        for (let row of this.bpConfirmationData) {
          if (row.item && row.item.id !== location.item.id) {
            //TODO usar função do chamado 0000545666
            this.$notify.textError(
              'Endereço ' + location.name + ' é incompatível com o item ' + row.item.description
            );
            return false;
          }
        }
      }

      return true;
    },

    confirmBatchPicking() {
      if (this.saving) return;

      if (!this.selectedLocation || !this.selectedLocation.id) {
        this.$notify.textError('Endereço de destino não informado');
        return;
      }

      if (!this.validateLocationItem(this.selectedLocation)) {
        return;
      }

      if (this.batchPickingWarehouse) {
        this.selectedLocationsPerWarehouse[this.batchPickingWarehouse.id] = this.selectedLocation;
      }

      shared.setLocalStorageObj(DESTINATION_LOCATION_KEY, this.selectedLocationsPerWarehouse);

      this.bpdto.resultOrder.destinationLocation = this.selectedLocation;
      this.updateSaving(true);
      httpClient.post(`${process.env.VUE_APP_API_URL}orders/batch-picking/confirm`, this.bpdto)
        .then(({ data }) => {
          this.updateSaving(false);
          this.$notify.success('Romaneio criado com sucesso');
          this.spliceSelectedOrders();
          this.$bvModal.hide('batch-picking-confirmation-modal');
          shared.setLocalStorageString('batch-picking-buttons/shouldOpenGeneratedOrder', this.shouldOpenGeneratedOrder);

          if (this.shouldOpenGeneratedOrder) {
            this.openOrderInfoModal(data.data);
          }
        })
        .catch((error) => {
          this.updateSaving(false);
          if (error.message) {
            this.$notify.error(error)
          }
        });
    }
  }
}
</script>

<style>
</style>
